import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { api } from '../api/client'

/**
 * The nav badges (HOT count, pipeline count). The prototype recomputed these from
 * localStorage on every tab switch; with server state they live in a small context that
 * any view can refresh after a mutation (pursue, remove, scan), so the badges never lag
 * the data the way a purely per-view fetch would.
 */
interface CountsState {
  hot: number
  pipeline: number
  refresh: () => Promise<void>
}

const CountsContext = createContext<CountsState | null>(null)

export function CountsProvider({ children }: { children: ReactNode }) {
  const [hot, setHot] = useState(0)
  const [pipeline, setPipeline] = useState(0)

  const refresh = useCallback(async () => {
    // Badges are decoration — if either call fails the views will surface the real error.
    try {
      const [opps, pipe] = await Promise.all([api.opportunities(), api.pipeline()])
      setHot(opps.filter((o) => o.band === 'HOT').length)
      setPipeline(pipe.length)
    } catch {
      /* keep the last known numbers */
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return <CountsContext.Provider value={{ hot, pipeline, refresh }}>{children}</CountsContext.Provider>
}

export function useCounts(): CountsState {
  const ctx = useContext(CountsContext)
  if (!ctx) throw new Error('useCounts must be used inside <CountsProvider>')
  return ctx
}
